/**
 * TRH Pillow Configurator - Browser Module
 * =========================================================================
 * Manages pillow fabric selection, yardage chart lookup, and multi-item
 * add-to-cart for Made-to-Order pillow products (Throw, Lumbar, Bolster).
 *
 * Pillow variants already have correct prices in Shopify. This module adds:
 *   1. Fabric selection via the shared TRHFabricBrowser module
 *   2. Yardage chart lookup based on pillow type + size + edge finish
 *   3. Multi-line-item add-to-cart (pillow variant + fabric + optional trim/contrast)
 *
 * Dependencies:
 *   - window.TRHFabricBrowser (shared fabric/trim selection module)
 *
 * Exposes:
 *   - window.TRHPillowConfigurator.init(config)
 *   - window.TRHPillowConfigurator.getYardage()
 *   - window.TRHPillowConfigurator.getState()
 *
 * @module TRHPillowConfigurator
 * @version 1.0.0
 */
(function () {
  'use strict';

  /* =========================================================================
   * CONSTANTS
   * ========================================================================= */

  /** @const {string} Log prefix for console messages */
  var LOG_PREFIX = '[TRHPillowConfigurator]';

  /** @const {number} Default fabric width in inches (all chart entries use 54") */
  var DEFAULT_FABRIC_WIDTH = 54;

  /** @const {number} Minimum fabric order in yards */
  var MIN_FABRIC_YARDS = 3;

  /** @const {number} Minimum trim order in yards */
  var MIN_TRIM_YARDS = 5;

  /** @const {number} Minimum contrast fabric order in yards */
  var MIN_CONTRAST_YARDS = 3;

  /** @const {string} Yardage disclaimer note */
  var YARDAGE_NOTE = 'Yardage is based on standard 54\u201d wide fabric and is sold in whole yards. Some fabrics and trims have their own minimum order, shown when you select them. If you have questions about a specific fabric\u2019s width or repeat, please reach out.';

  /* TRH yardage note 2026-10-26 (Rachel t360u): short, accurate note; names the selected fabric's / trim's own minimum (TRHFabricMin + trimMinYards) */
  function yardageNote(noStoreFabric) {
    var storeFabric = !noStoreFabric && !_ownFabric && _selectedFabric;
    var unitWord = (storeFabric && _FM().info(_selectedFabric).unit === 'meter') ? 'meters' : 'yards';
    var parts = [_ownFabric ? 'Yardage is based on standard 54\u201d wide fabric.' : 'Yardage is based on standard 54\u201d wide fabric and is sold in whole ' + unitWord + '.'];
    var named = false;
    if (storeFabric) {
      var fMin = _FM().info(_selectedFabric).min;
      if (fMin > 1) { parts.push('This fabric has a minimum order of ' + _FM().label(fMin, _selectedFabric) + '.'); named = true; }
    }
    if (_selectedTrim && !_ownTrim && trimMinYards(_selectedTrim) > 1) {
      parts.push('This trim has a minimum order of ' + trimMinYards(_selectedTrim) + ' yards.'); named = true;
    }
    if (!named && !storeFabric) parts.push('Some fabrics and trims have their own minimum order, shown when you select them.');
    parts.push('If you have questions about a specific fabric\u2019s width or repeat, please reach out.');
    return parts.join(' ');
  }

  /* =========================================================================
   * YARDAGE CHART
   * =========================================================================
   * Lookup table keyed by "{Type} {Size}" (e.g., "Lumbar 10 x 18").
   * Each entry contains yardage values for all edge finish scenarios.
   *
   * Fields:
   *   noWelt       - Fabric yards when edge finish is "No Welt"
   *   selfWelt     - Fabric yards when edge finish is "Self Welt"
   *   contrastWelt - Additional fabric yards for contrast welt piece
   *   trim         - Trim yardage for trim-based edge finishes
   */
  var YARDAGE_CHART = {
    // -- LUMBAR PILLOW SIZES --------------------------------------------------
    'Lumbar 10 x 18':  { noWelt: 0.33, selfWelt: 0.58, contrastWelt: 0.25, trim: 1.36 },
    'Lumbar 10 x 20':  { noWelt: 0.33, selfWelt: 0.58, contrastWelt: 0.25, trim: 1.75 },
    'Lumbar 10 x 22':  { noWelt: 0.33, selfWelt: 0.58, contrastWelt: 0.25, trim: 1.87 },
    'Lumbar 12 x 18':  { noWelt: 0.39, selfWelt: 0.64, contrastWelt: 0.25, trim: 1.75 },
    'Lumbar 12 x 20':  { noWelt: 0.39, selfWelt: 0.69, contrastWelt: 0.30, trim: 1.87 },
    'Lumbar 12 x 22':  { noWelt: 0.39, selfWelt: 0.69, contrastWelt: 0.30, trim: 1.98 },
    'Lumbar 12 x 34':  { noWelt: 0.78, selfWelt: 1.13, contrastWelt: 0.35, trim: 2.68 },
    'Lumbar 12 x 40':  { noWelt: 0.78, selfWelt: 1.13, contrastWelt: 0.35, trim: 3.03 },
    'Lumbar 14 x 18':  { noWelt: 0.44, selfWelt: 0.69, contrastWelt: 0.25, trim: 1.87 },
    'Lumbar 14 x 20':  { noWelt: 0.44, selfWelt: 0.74, contrastWelt: 0.30, trim: 1.98 },
    'Lumbar 14 x 26':  { noWelt: 0.89, selfWelt: 1.19, contrastWelt: 0.30, trim: 2.33 },
    'Lumbar 14 x 46':  { noWelt: 0.89, selfWelt: 1.34, contrastWelt: 0.45, trim: 3.50 },
    'Lumbar 18 x 22':  { noWelt: 0.56, selfWelt: 0.86, contrastWelt: 0.30, trim: 2.33 },
    'Lumbar 20 x 26':  { noWelt: 1.22, selfWelt: 1.52, contrastWelt: 0.30, trim: 2.68 },
    'Lumbar 20 x 30':  { noWelt: 1.22, selfWelt: 1.57, contrastWelt: 0.35, trim: 2.92 },
    'Lumbar 20 x 36':  { noWelt: 1.22, selfWelt: 1.62, contrastWelt: 0.40, trim: 3.27 },
    'Lumbar 25 x 34':  { noWelt: 1.50, selfWelt: 1.95, contrastWelt: 0.45, trim: 3.44 },

    // -- THROW PILLOW SIZES ---------------------------------------------------
    'Throw 12 x 12':  { noWelt: 0.34, selfWelt: 0.59, contrastWelt: 0.25, trim: 1.40 },
    'Throw 14 x 14':  { noWelt: 0.44, selfWelt: 0.69, contrastWelt: 0.25, trim: 1.63 },
    'Throw 16 x 16':  { noWelt: 0.50, selfWelt: 0.75, contrastWelt: 0.25, trim: 1.87 },
    'Throw 18 x 18':  { noWelt: 0.56, selfWelt: 0.86, contrastWelt: 0.30, trim: 2.10 },
    'Throw 20 x 20':  { noWelt: 0.61, selfWelt: 0.91, contrastWelt: 0.30, trim: 2.33 },
    'Throw 22 x 22':  { noWelt: 0.67, selfWelt: 1.02, contrastWelt: 0.35, trim: 2.57 },
    'Throw 24 x 24':  { noWelt: 0.72, selfWelt: 1.12, contrastWelt: 0.40, trim: 2.80 },
    'Throw 26 x 26':  { noWelt: 1.56, selfWelt: 2.01, contrastWelt: 0.45, trim: 3.03 },
    'Throw 28 x 28':  { noWelt: 1.67, selfWelt: 2.12, contrastWelt: 0.45, trim: 3.27 },

    // -- BOLSTER PILLOW SIZES -------------------------------------------------
    'Bolster 6 x 14':  { noWelt: 0.35, selfWelt: 0.55, contrastWelt: 0.20, trim: 1.20 },
    'Bolster 6 x 18':  { noWelt: 0.45, selfWelt: 0.65, contrastWelt: 0.20, trim: 1.20 },
    'Bolster 6 x 22':  { noWelt: 0.50, selfWelt: 0.70, contrastWelt: 0.20, trim: 1.20 },
    'Bolster 6 x 28':  { noWelt: 0.60, selfWelt: 0.80, contrastWelt: 0.20, trim: 1.20 },
    'Bolster 6 x 34':  { noWelt: 0.65, selfWelt: 0.85, contrastWelt: 0.20, trim: 1.20 },
    'Bolster 6 x 48':  { noWelt: 0.67, selfWelt: 0.87, contrastWelt: 0.20, trim: 1.20 },
    'Bolster 6 x 60':  { noWelt: 1.10, selfWelt: 1.30, contrastWelt: 0.20, trim: 1.20 },
    'Bolster 6 x 72':  { noWelt: 1.20, selfWelt: 1.40, contrastWelt: 0.20, trim: 1.20 },
    'Bolster 7 x 14':  { noWelt: 0.35, selfWelt: 0.55, contrastWelt: 0.20, trim: 1.35 },
    'Bolster 7 x 18':  { noWelt: 0.45, selfWelt: 0.65, contrastWelt: 0.20, trim: 1.35 },
    'Bolster 7 x 22':  { noWelt: 0.50, selfWelt: 0.70, contrastWelt: 0.20, trim: 1.35 },
    'Bolster 7 x 28':  { noWelt: 0.63, selfWelt: 0.83, contrastWelt: 0.20, trim: 1.35 },
    'Bolster 7 x 34':  { noWelt: 0.66, selfWelt: 0.86, contrastWelt: 0.20, trim: 1.35 },
    'Bolster 7 x 48':  { noWelt: 0.90, selfWelt: 1.10, contrastWelt: 0.20, trim: 1.35 },
    'Bolster 7 x 60':  { noWelt: 1.15, selfWelt: 1.35, contrastWelt: 0.20, trim: 1.35 },
    'Bolster 7 x 72':  { noWelt: 1.25, selfWelt: 1.45, contrastWelt: 0.20, trim: 1.35 },
    'Bolster 8 x 14':  { noWelt: 0.33, selfWelt: 0.58, contrastWelt: 0.25, trim: 1.50 },
    'Bolster 8 x 18':  { noWelt: 0.48, selfWelt: 0.73, contrastWelt: 0.25, trim: 1.50 },
    'Bolster 8 x 22':  { noWelt: 0.50, selfWelt: 0.75, contrastWelt: 0.25, trim: 1.50 },
    'Bolster 8 x 28':  { noWelt: 0.80, selfWelt: 1.05, contrastWelt: 0.25, trim: 1.50 },
    'Bolster 8 x 34':  { noWelt: 0.80, selfWelt: 1.05, contrastWelt: 0.25, trim: 1.50 },
    'Bolster 8 x 48':  { noWelt: 1.00, selfWelt: 1.25, contrastWelt: 0.25, trim: 1.50 },
    'Bolster 8 x 60':  { noWelt: 1.20, selfWelt: 1.45, contrastWelt: 0.25, trim: 1.50 },
    'Bolster 8 x 72':  { noWelt: 1.30, selfWelt: 1.55, contrastWelt: 0.25, trim: 1.70 },
    'Bolster 9 x 14':  { noWelt: 0.40, selfWelt: 0.65, contrastWelt: 0.25, trim: 1.70 },
    'Bolster 9 x 18':  { noWelt: 0.55, selfWelt: 0.80, contrastWelt: 0.25, trim: 1.70 },
    'Bolster 9 x 22':  { noWelt: 0.65, selfWelt: 0.90, contrastWelt: 0.25, trim: 1.70 },
    'Bolster 9 x 28':  { noWelt: 0.83, selfWelt: 1.08, contrastWelt: 0.25, trim: 1.70 },
    'Bolster 9 x 34':  { noWelt: 0.83, selfWelt: 1.08, contrastWelt: 0.25, trim: 1.70 },
    'Bolster 9 x 48':  { noWelt: 1.10, selfWelt: 1.35, contrastWelt: 0.25, trim: 1.70 },
    'Bolster 9 x 60':  { noWelt: 1.30, selfWelt: 1.55, contrastWelt: 0.25, trim: 1.70 },
    'Bolster 9 x 72':  { noWelt: 1.40, selfWelt: 1.65, contrastWelt: 0.25, trim: 1.70 }
  };

  /* =========================================================================
   * EDGE FINISH DEFINITIONS
   * =========================================================================
   * Maps edge finish option values to their behavior configuration.
   *
   * Fields:
   *   fabricColumn    - Which yardage column to use for main fabric
   *   needsTrim       - Whether trim selection UI should be shown
   *   needsContrast   - Whether contrast fabric selection UI should be shown
   *   isCOM           - Whether this is Customer's Own Material (hides fabric browse)
   */
  var EDGE_FINISH_MAP = {
    'No Welt':              { fabricColumn: 'noWelt',   needsTrim: false, needsContrast: false, isCOM: false },
    /* TRH welt 2026-10-26 (Rachel): only a contrast welt adds yardage, so self
     * welt uses the base (noWelt) column. Reverted 2026-10-26 (Rachel): pillows keep the selfWelt allowance column. */
    'Self Welt':            { fabricColumn: 'selfWelt',   needsTrim: false, needsContrast: false, isCOM: false },
    'Trim':                 { fabricColumn: 'noWelt',   needsTrim: true,  needsContrast: false, isCOM: false },
    'Turkish Pucker':       { fabricColumn: 'noWelt',   needsTrim: true,  needsContrast: false, isCOM: false },
    'Contrast Fabric Welt': { fabricColumn: 'noWelt',   needsTrim: false, needsContrast: true,  isCOM: false },
    'COM':                  { fabricColumn: 'noWelt',   needsTrim: false, needsContrast: false, isCOM: true  }
  };

  /* =========================================================================
   * MODULE STATE
   * ========================================================================= */

  /** @type {Object|null} Configuration from init() */
  var _config = null;

  /** @type {Object|null} Currently selected main fabric from TRHFabricBrowser */
  var _selectedFabric = null;

  /** @type {Object|null} Currently selected trim from TRHFabricBrowser */
  var _selectedTrim = null;

  /** @type {Object|null} Currently selected contrast fabric from TRHFabricBrowser */
  var _selectedContrastFabric = null;

  /**
   * Where the next catalog-popup fabric selection should land: 'fabric'
   * (the main fabric) or 'contrast'. The shared TRHFabricBrowser catalog
   * only knows 'fabric'/'trim', so without this flag a selection made via
   * the contrast "Browse Fabrics" button would overwrite the main fabric.
   * @type {string}
   */
  var _fabricBrowseTarget = 'fabric';

  /** @type {string|null} Currently detected size string (e.g., "18 x 18") */
  var _currentSize = null;

  /** @type {string|null} Currently detected edge finish (e.g., "Self Welt") */
  var _currentEdgeFinish = null;

  /** @type {number|null} Currently selected Shopify variant ID */
  var _currentVariantId = null;

  /** @type {boolean} Whether the customer is providing their own fabric */
  var _ownFabric = false;

  /** @type {boolean} Whether the customer is providing their own trim */
  var _ownTrim = false;

  /** @type {boolean} Whether the customer is providing their own contrast fabric */
  var _ownContrast = false;

  /** @type {boolean} Whether a cart submission is in progress */
  var _isSubmitting = false;

  /** @type {Object|null} COM (Customer's Own Material) field values */
  var _comFields = null;

  /* =========================================================================
   * DOM HELPERS
   * ========================================================================= */

  /**
   * Retrieves a DOM element by ID, returning null if not found.
   * @param {string} id - Element ID
   * @returns {HTMLElement|null}
   */
  function $(id) {
    return document.getElementById(id);
  }

  /**
   * Rounds a number to the specified decimal places.
   * @param {number} value - Number to round
   * @param {number} decimals - Number of decimal places
   * @returns {number} Rounded value
   */
  function roundTo(value, decimals) {
    var factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }

  /* =========================================================================
   * VARIANT OPTION DETECTION
   * =========================================================================
   * Shopify product pages render variant options as <select> elements or
   * radio button groups. We need to detect the current Size and Edge Finish
   * values whenever a customer changes their selection.
   */

  /**
   * Reads the currently selected value for a given Shopify variant option name
   * (e.g., "Size", "Edge Finish"). Searches through common Shopify theme
   * selector patterns: <select> elements and radio button groups.
   *
   * @param {string} optionName - The option name to look for (case-insensitive)
   * @returns {string|null} The selected value, or null if not found
   */
  function getSelectedOptionValue(optionName) {
    var lowerName = optionName.toLowerCase();

    // Strategy 1: Look for <select> elements whose associated <label>
    // contains the option name
    var selects = document.querySelectorAll(
      'select[name*="option"], select[data-option-name], .product-form__input select'
    );
    for (var i = 0; i < selects.length; i++) {
      var select = selects[i];

      // Check data-option-name attribute
      var dataName = select.getAttribute('data-option-name');
      if (dataName && dataName.toLowerCase() === lowerName) {
        return select.value;
      }

      // Check the associated label text
      var label = _findLabelForElement(select);
      if (label && label.toLowerCase().indexOf(lowerName) !== -1) {
        return select.value;
      }
    }

    // Strategy 2: Look for checked radio buttons in fieldsets/groups
    // labeled with the option name
    var fieldsets = document.querySelectorAll(
      'fieldset, .product-form__input, [data-option-name]'
    );
    for (var j = 0; j < fieldsets.length; j++) {
      var fieldset = fieldsets[j];

      var fsDataName = fieldset.getAttribute('data-option-name');
      var legendEl = fieldset.querySelector('legend');
      var fsLabel = fsDataName || (legendEl ? legendEl.textContent : '');

      if (fsLabel.toLowerCase().indexOf(lowerName) !== -1) {
        var checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
        if (checkedRadio) {
          return checkedRadio.value;
        }
      }
    }

    return null;
  }

  /**
   * Finds the label text associated with a form element. Checks for
   * <label> elements with matching `for` attribute, parent label wrappers,
   * and adjacent siblings.
   *
   * @param {HTMLElement} element - The form element
   * @returns {string|null} Label text, or null if not found
   * @private
   */
  function _findLabelForElement(element) {
    // Check for label with matching "for" attribute
    if (element.id) {
      var label = document.querySelector('label[for="' + element.id + '"]');
      if (label) return label.textContent.trim();
    }

    // Check parent label wrapper
    var parent = element.closest('label');
    if (parent) return parent.textContent.trim();

    // Check previous sibling label
    var prev = element.previousElementSibling;
    if (prev && prev.tagName === 'LABEL') return prev.textContent.trim();

    // Check parent container for a label child
    var container = element.closest('.product-form__input');
    if (container) {
      var containerLabel = container.querySelector('label');
      if (containerLabel) return containerLabel.textContent.trim();
    }

    return null;
  }

  /**
   * Reads the currently selected Shopify variant ID from the product form.
   * Looks for a hidden input or select with name="id" inside the product form.
   *
   * @returns {string|null} Numeric variant ID string, or null
   */
  function getSelectedVariantId() {
    var form = document.querySelector(
      'form[action*="/cart/add"], form.product-form, form[data-type="add-to-cart-form"]'
    );
    if (!form) return null;

    var idInput = form.querySelector('input[name="id"], select[name="id"]');
    if (idInput) return idInput.value;

    return null;
  }

  /* =========================================================================
   * YARDAGE LOOKUP
   * ========================================================================= */

  /**
   * Builds the yardage chart lookup key from the pillow type and size.
   *
   * @param {string} pillowType - 'throw', 'lumbar', or 'bolster' (case-insensitive)
   * @param {string} size - Size string from the variant option (e.g., "18 x 18")
   * @returns {string} Lookup key (e.g., "Throw 18 x 18")
   */
  function buildLookupKey(pillowType, size) {
    var type = pillowType.charAt(0).toUpperCase() + pillowType.slice(1).toLowerCase();
    // Strip inch marks (", ″, in, inches) from the size string
    var cleanSize = size.replace(/["″'']/g, '').replace(/\s*(in|inches)\b/gi, '').trim();
    // Normalize multiple spaces to single space
    cleanSize = cleanSize.replace(/\s+/g, ' ');
    return type + ' ' + cleanSize;
  }

  /**
   * Looks up yardage values from the chart for the given pillow type, size,
   * and edge finish combination.
   *
   * Returns an object describing fabric yardage for the main fabric, trim
   * yardage (if applicable), and contrast fabric yardage (if applicable).
   *
   * @param {string} pillowType - 'throw', 'lumbar', or 'bolster'
   * @param {string} size - Size string (e.g., "18 x 18")
   * @param {string} edgeFinish - Edge finish option value (e.g., "Self Welt", "Trim")
   * @returns {Object|null} Yardage result object, or null if not found
   * @returns {number} return.fabricYardage - Main fabric yardage (yards)
   * @returns {number} return.trimYardage - Trim yardage, 0 if not applicable
   * @returns {number} return.contrastYardage - Contrast fabric yardage, 0 if not applicable
   * @returns {string} return.lookupKey - The key used for the chart lookup
   */
  function lookupYardage(pillowType, size, edgeFinish) {
    var key = buildLookupKey(pillowType, size);
    var entry = YARDAGE_CHART[key];

    if (!entry) {
      console.warn(LOG_PREFIX, 'No yardage chart entry for key:', key);
      return null;
    }

    var finish = EDGE_FINISH_MAP[edgeFinish];
    if (!finish) {
      console.warn(LOG_PREFIX, 'Unknown edge finish:', edgeFinish);
      return null;
    }

    var fabricYardage = roundTo(entry[finish.fabricColumn], 2);
    var trimYardage = finish.needsTrim ? roundTo(entry.trim, 2) : 0;
    var contrastYardage = finish.needsContrast ? roundTo(entry.contrastWelt, 2) : 0;

    return {
      fabricYardage: fabricYardage,
      trimYardage: trimYardage,
      contrastYardage: contrastYardage,
      lookupKey: key
    };
  }

  /* =========================================================================
   * VARIANT CHANGE DETECTION
   * =========================================================================
   * We listen for changes to Shopify variant option selectors to detect
   * the current size and edge finish, then update the yardage display
   * and visibility of trim/contrast/COM UI sections.
   */

  /**
   * Reads the current variant options from the DOM and updates internal state.
   * Called whenever a variant option selector changes.
   */
  function syncVariantOptions() {
    // Read the Size option
    var size = getSelectedOptionValue('Size');
    if (size) {
      _currentSize = size.trim();
    }

    // Read the Edge Finish / Seam Finish option (products may use either name)
    var edgeFinish = getSelectedOptionValue('Seam Finish') || getSelectedOptionValue('Edge Finish');
    if (edgeFinish) {
      _currentEdgeFinish = edgeFinish.trim();
    }

    // Read the variant ID
    var variantId = getSelectedVariantId();
    if (variantId) {
      _currentVariantId = variantId;
    }

    // Update the UI based on the new state
    updateEdgeFinishUI();
    updateYardageDisplay();
  }

  /**
   * Updates the visibility of trim selection, contrast fabric selection,
   * and COM fields based on the currently selected edge finish.
   */
  function updateEdgeFinishUI() {
    if (!_currentEdgeFinish) return;

    var finish = EDGE_FINISH_MAP[_currentEdgeFinish];
    if (!finish) return;

    // Trim selection UI visibility
    var trimSection = $('trh-pillow-trim-section');
    if (trimSection) {
      trimSection.style.display = finish.needsTrim ? 'block' : 'none';
    }

    // Contrast fabric selection UI visibility
    var contrastSection = $('trh-pillow-contrast-section');
    if (contrastSection) {
      contrastSection.style.display = finish.needsContrast ? 'block' : 'none';
    }

    // COM fields visibility (hide fabric browse, show custom fields)
    var fabricSection = $('trh-pillow-fabric-section');
    var comSection = $('trh-pillow-com-section');
    if (fabricSection) {
      fabricSection.style.display = finish.isCOM ? 'none' : 'block';
    }
    if (comSection) {
      comSection.style.display = finish.isCOM ? 'block' : 'none';
    }
  }

  /**
   * Handles the COM / Off-Site Fabric checkbox toggle.
   * Hides fabric browse and shows COM text fields (or vice versa).
   */
  function handleOwnFabricToggle() {
    var cb = $('pillowOwnFabric');
    _ownFabric = cb ? cb.checked : false;

    var browseSection = $('trh-pillow-fabric-browse-section');
    var comSection = $('trh-pillow-com-section');

    if (browseSection) browseSection.style.display = _ownFabric ? 'none' : 'block';
    if (comSection) comSection.style.display = _ownFabric ? 'block' : 'none';

    if (_ownFabric) {
      _selectedFabric = null;
    }

    updateYardageDisplay();
  }

  /**
   * Handles the COM / Off-Site Trim checkbox toggle.
   * Hides trim browse and shows COM text fields (or vice versa).
   */
  function handleOwnTrimToggle() {
    var cb = $('pillowOwnTrim');
    _ownTrim = cb ? cb.checked : false;

    var browseSection = $('trh-pillow-trim-browse-section');
    var comSection = $('trh-pillow-trim-com-section');

    if (browseSection) browseSection.style.display = _ownTrim ? 'none' : 'block';
    if (comSection) comSection.style.display = _ownTrim ? 'block' : 'none';

    if (_ownTrim) {
      _selectedTrim = null;
    }

    updateYardageDisplay();
  }

  /**
   * Handles the COM / Off-Site Contrast Fabric checkbox toggle.
   * Hides contrast browse and shows COM text fields (or vice versa).
   */
  function handleOwnContrastToggle() {
    var cb = $('pillowOwnContrast');
    _ownContrast = cb ? cb.checked : false;

    var browseSection = $('trh-pillow-contrast-browse-section');
    var comSection = $('trh-pillow-contrast-com-section');

    if (browseSection) browseSection.style.display = _ownContrast ? 'none' : 'block';
    if (comSection) comSection.style.display = _ownContrast ? 'block' : 'none';

    if (_ownContrast) {
      _selectedContrastFabric = null;
    }

    updateYardageDisplay();
  }

  /**
   * Updates the yardage display element with the current calculated yardage
   * values. Shows fabric yardage, and trim or contrast yardage when applicable.
   */
  /**
   * Applies the minimum yardage rule: fabric >= 3yd, trim >= 5yd, contrast >= 3yd.
   * @param {number} yards - Raw yardage from chart
   * @param {string} type - 'fabric', 'trim', or 'contrast'
   * @returns {number} Yardage with minimum applied
   */
  /* TRH trim min 2026-10-26 (Rachel): only The ReBorn House (TRH) trims have the 5 yd minimum; other
     brands' trims have none (sold by the yard, 1+; a min-N tag on a trim still applies) */
  function trimMinYards(t) {
    if (!t) return 1;
    var n = 0;
    (t.tags || []).forEach(function (x) { x = String(x).toLowerCase(); if (x.slice(0, 4) === 'min-') { var v = parseInt(x.slice(4), 10); if (v > n) n = v; } });
    if (t.vendor === 'The ReBorn House' || /^TRH\d/i.test(String(t.title || ''))) return Math.max(5, n);
    return n > 1 ? n : 1;
  }
  function applyMinimum(yards, type) {
    var ceiled = Math.ceil(yards);
    if (type === 'trim') return Math.max(ceiled, trimMinYards(_selectedTrim));
    /* TRH fabric min fix 2026-10-26: store fabric -> its own minimum and unit */
    if (type === 'contrast') return _FM().qty(yards, _selectedContrastFabric);
    return _FM().qty(yards, _selectedFabric);
  }

  /** Fabric object for a yardage type ('fabric' or 'contrast'). */
  function fabricFor(type) {
    return type === 'contrast' ? _selectedContrastFabric : _selectedFabric;
  }

  /* TRH fabric min fix 2026-10-26: per-fabric minimum + unit (see TRHFabricMin in trh-fabric-browser.js) */
  function _FM() {
    return window.TRHFabricMin || {
      qty: function (y) { return Math.max(Math.ceil(y), 1); },
      info: function () { return { min: 1, unit: 'yard' }; },
      label: function (n) { return n + ' yards'; },
      abbr: function () { return 'yd'; },
      per: function () { return '/yd'; }
    };
  }

  function updateYardageDisplay() {
    var displayEl = $('trh-pillow-yardage-display');
    if (!displayEl) return;

    if (!_currentSize || !_currentEdgeFinish || !_config) {
      displayEl.innerHTML = '';
      displayEl.style.display = 'none';
      updatePricingDisplay();
      return;
    }

    var yardage = lookupYardage(_config.pillowType, _currentSize, _currentEdgeFinish);
    if (!yardage) {
      displayEl.innerHTML = '<p style="color:#999;font-style:italic;">Yardage data not available for this size/finish combination.</p>';
      displayEl.style.display = 'block';
      updatePricingDisplay();
      return;
    }

    var finish = EDGE_FINISH_MAP[_currentEdgeFinish];
    var lines = [];
    var userQty = getFormQuantity();

    /* COM / Off-Site Fabric: show exact calculated yardage (no minimum, no ceiling).
       Store fabric: apply minimum order + ceiling as before. */
    if (!finish.isCOM) {
      var totalFabric = yardage.fabricYardage * userQty;
      if (_ownFabric) {
        /* COM — exact yardage, rounded to 2 decimals */
        var comFabricQty = roundTo(totalFabric, 2);
        lines.push('<p><strong>Fabric Required:</strong> ' + comFabricQty + ' yards</p>');
      } else {
        /* Store fabric — apply minimum order */
        var fabricQty = applyMinimum(totalFabric, 'fabric');
        var fabricMin = _FM().info(_selectedFabric).min;
        var fabricNote = fabricMin > 1 && fabricQty <= fabricMin
          ? ' <span style="color:#888;font-size:12px;">(min ' + fabricMin + ' ' + _FM().abbr(_selectedFabric) + ' order)</span>'
          : '';
        lines.push('<p><strong>Fabric Required:</strong> ' + _FM().label(fabricQty, _selectedFabric) + fabricNote + '</p>');
      }
    }

    if (yardage.trimYardage > 0) {
      var totalTrim = yardage.trimYardage * userQty;
      if (_ownTrim) {
        var comTrimQty = roundTo(totalTrim, 2);
        lines.push('<p><strong>Trim Required:</strong> ' + comTrimQty + ' yards</p>');
      } else {
        var trimQty = applyMinimum(totalTrim, 'trim');
        var trimNote = (trimMinYards(_selectedTrim) > 1 && totalTrim < trimMinYards(_selectedTrim))
          ? ' <span style="color:#888;font-size:12px;">(min ' + trimMinYards(_selectedTrim) + ' yd order)</span>'
          : '';
        lines.push('<p><strong>Trim Required:</strong> ' + trimQty + ' yards' + trimNote + '</p>');
      }
    }

    if (yardage.contrastYardage > 0) {
      var totalContrast = yardage.contrastYardage * userQty;
      if (_ownContrast) {
        var comContrastQty = roundTo(totalContrast, 2);
        lines.push('<p><strong>Contrast Fabric Required:</strong> ' + comContrastQty + ' yards</p>');
      } else {
        var contrastQty = applyMinimum(totalContrast, 'contrast');
        var contrastMin = _FM().info(_selectedContrastFabric).min;
        var contrastNote = contrastMin > 1 && contrastQty <= contrastMin
          ? ' <span style="color:#888;font-size:12px;">(min ' + contrastMin + ' ' + _FM().abbr(_selectedContrastFabric) + ' order)</span>'
          : '';
        lines.push('<p><strong>Contrast Fabric Required:</strong> ' + _FM().label(contrastQty, _selectedContrastFabric) + contrastNote + '</p>');
      }
    }

    if (lines.length > 0) {
      lines.push('<p style="margin-top:6px;font-size:11px;color:#888;font-style:italic;">' + yardageNote(finish.isCOM) + '</p>');
      displayEl.innerHTML = lines.join('');
      displayEl.style.display = 'block';
    } else {
      displayEl.innerHTML = '';
      displayEl.style.display = 'none';
    }

    updatePricingDisplay();
  }

  /**
   * Updates the price breakdown display showing pillow + fabric + trim = total.
   */
  function updatePricingDisplay() {
    var pricingEl = $('trh-pillow-pricing-display');
    if (!pricingEl) return;

    if (!_currentSize || !_currentEdgeFinish || !_config) {
      pricingEl.style.display = 'none';
      return;
    }

    var finish = EDGE_FINISH_MAP[_currentEdgeFinish];
    if (!finish) { pricingEl.style.display = 'none'; return; }

    var yardage = lookupYardage(_config.pillowType, _currentSize, _currentEdgeFinish);
    if (!yardage) { pricingEl.style.display = 'none'; return; }

    // Get pillow variant price from the page
    var pillowPrice = getVariantPrice();
    var rows = [];
    var total = pillowPrice;

    rows.push(row('Pillow (sew + insert)', pillowPrice));

    if (_ownFabric || finish.isCOM) {
      rows.push('<div class="trh-pillow-fabric__pricing-row"><span>Fabric (COM / Off-Site)</span><span>excluded</span></div>');
    } else if (_selectedFabric && _selectedFabric.price > 0) {
      var fabricQty = applyMinimum(yardage.fabricYardage, 'fabric');
      var fabricCost = Math.ceil(fabricQty * _selectedFabric.price);
      total += fabricCost;
      rows.push(row(_selectedFabric.title + ' (' + fabricQty + ' ' + _FM().abbr(_selectedFabric) + ' @ $' + _selectedFabric.price.toFixed(2) + _FM().per(_selectedFabric) + ')', fabricCost));
    }

    if (finish.needsTrim) {
      if (_ownTrim) {
        rows.push('<div class="trh-pillow-fabric__pricing-row"><span>Trim (COM / Off-Site)</span><span>excluded</span></div>');
      } else if (_selectedTrim && _selectedTrim.price > 0 && yardage.trimYardage > 0) {
        var trimQty = applyMinimum(yardage.trimYardage, 'trim');
        var trimCost = Math.ceil(trimQty * _selectedTrim.price);
        total += trimCost;
        rows.push(row(_selectedTrim.title + ' (' + trimQty + ' yd @ $' + _selectedTrim.price.toFixed(2) + '/yd)', trimCost));
      }
    }

    if (finish.needsContrast) {
      if (_ownContrast) {
        rows.push('<div class="trh-pillow-fabric__pricing-row"><span>Contrast Fabric (COM / Off-Site)</span><span>excluded</span></div>');
      } else if (_selectedContrastFabric && _selectedContrastFabric.price > 0 && yardage.contrastYardage > 0) {
        var contrastQty = applyMinimum(yardage.contrastYardage, 'contrast');
        var contrastCost = Math.ceil(contrastQty * _selectedContrastFabric.price);
        total += contrastCost;
        rows.push(row(_selectedContrastFabric.title + ' (' + contrastQty + ' ' + _FM().abbr(_selectedContrastFabric) + ' @ $' + _selectedContrastFabric.price.toFixed(2) + _FM().per(_selectedContrastFabric) + ')', contrastCost));
      }
    }

    if (rows.length <= 1 && !_selectedFabric && !_ownFabric && !finish.isCOM) {
      // Only pillow price, no fabric selected yet — don't show
      pricingEl.style.display = 'none';
      return;
    }

    rows.push('<div class="trh-pillow-fabric__pricing-total"><span>Estimated Total</span><span>$' + Math.ceil(total) + '</span></div>');
    pricingEl.innerHTML = rows.join('');
    pricingEl.style.display = 'block';
  }

  /** Builds a single pricing row HTML string. */
  function row(label, amount) {
    return '<div class="trh-pillow-fabric__pricing-row"><span>' + label + '</span><span>$' + Math.ceil(amount) + '</span></div>';
  }

  /**
   * Reads the current variant price from the product page DOM.
   * @returns {number} Price in dollars
   */
  function getVariantPrice() {
    var priceEl = document.querySelector('.price-item--regular, .price .money, .product__price .money, [data-product-price]');
    if (priceEl) {
      var text = priceEl.textContent.replace(/[^0-9.]/g, '');
      var val = parseFloat(text);
      if (!isNaN(val)) return val;
    }
    return 0;
  }

  /* =========================================================================
   * ADD TO CART
   * =========================================================================
   * Intercepts the product form submission and sends a multi-item POST
   * to /cart/add.js containing:
   *   1. The pillow variant (with fabric/trim metadata as line item properties)
   *   2. The fabric variant (with calculated yardage as quantity)
   *   3. Optionally: trim variant or contrast fabric variant
   */

  /**
   * Validates that all required selections have been made before allowing
   * add-to-cart. Returns an error message string if invalid, or null if valid.
   *
   * @returns {string|null} Error message, or null if valid
   */
  function validateForCart() {
    if (!_currentVariantId) {
      return 'Please select a pillow size and edge finish.';
    }

    if (!_currentSize || !_currentEdgeFinish) {
      return 'Please select a pillow size and edge finish.';
    }

    var finish = EDGE_FINISH_MAP[_currentEdgeFinish];
    if (!finish) {
      return 'Unknown edge finish selected.';
    }

    // COM requires custom material details
    if (finish.isCOM) {
      var comName = $('trh-pillow-com-name');
      if (!comName || !comName.value.trim()) {
        return 'Please enter your fabric name/description for COM.';
      }
      return null;
    }

    // Main fabric is required unless COM checkbox is checked
    if (!_ownFabric && !_selectedFabric) {
      return 'Please select a fabric or check COM / Off-Site Fabric.';
    }

    // Trim is required for trim-based finishes unless COM trim is checked
    if (finish.needsTrim && !_ownTrim && !_selectedTrim) {
      return 'Please select a trim or check COM / Off-Site Trim.';
    }

    // Contrast fabric is required for contrast welt (unless COM)
    if (finish.needsContrast && !_ownContrast && !_selectedContrastFabric) {
      return 'Please select a contrast fabric or check COM / Off-Site Contrast Fabric.';
    }

    // Verify yardage data exists for this combination
    var yardage = lookupYardage(_config.pillowType, _currentSize, _currentEdgeFinish);
    if (!yardage) {
      return 'Yardage data not available for this size/finish combination.';
    }

    return null;
  }

  /**
   * Reads the quantity input value from the product form. Falls back to 1
   * if not found or invalid.
   *
   * @returns {number} User-specified quantity (minimum 1)
   */
  function getFormQuantity() {
    var form = document.querySelector(
      'form[action*="/cart/add"], form.product-form, form[data-type="add-to-cart-form"]'
    );
    if (!form) return 1;

    var qtyInput = form.querySelector('input[name="quantity"]');
    if (qtyInput) {
      var val = parseInt(qtyInput.value, 10);
      return isNaN(val) || val < 1 ? 1 : val;
    }

    return 1;
  }

  /**
   * Collects COM (Customer's Own Material) field values from the DOM.
   *
   * @returns {Object} COM field values
   * @returns {string} return.name - Fabric name/description
   * @returns {string} return.brand - Fabric brand
   * @returns {string} return.link - Link to fabric online
   * @returns {string} return.notes - Additional notes
   */
  function collectCOMFields() {
    return {
      name: ($('trh-pillow-com-name') && $('trh-pillow-com-name').value.trim()) || '',
      brand: ($('trh-pillow-com-brand') && $('trh-pillow-com-brand').value.trim()) || '',
      color: ($('trh-pillow-com-color') && $('trh-pillow-com-color').value.trim()) || '',
      link: ($('trh-pillow-com-link') && $('trh-pillow-com-link').value.trim()) || '',
      notes: ($('trh-pillow-com-notes') && $('trh-pillow-com-notes').value.trim()) || ''
    };
  }

  /**
   * Collects COM trim field values from the DOM.
   * @returns {Object} COM trim field values
   */
  function collectTrimCOMFields() {
    return {
      name: ($('trh-pillow-trim-com-name') && $('trh-pillow-trim-com-name').value.trim()) || '',
      brand: ($('trh-pillow-trim-com-brand') && $('trh-pillow-trim-com-brand').value.trim()) || '',
      color: ($('trh-pillow-trim-com-color') && $('trh-pillow-trim-com-color').value.trim()) || '',
      link: ($('trh-pillow-trim-com-link') && $('trh-pillow-trim-com-link').value.trim()) || '',
      notes: ($('trh-pillow-trim-com-notes') && $('trh-pillow-trim-com-notes').value.trim()) || ''
    };
  }

  /**
   * Collects COM contrast fabric field values from the DOM.
   * @returns {Object} COM contrast fabric field values
   */
  function collectContrastCOMFields() {
    return {
      name: ($('trh-pillow-contrast-com-name') && $('trh-pillow-contrast-com-name').value.trim()) || '',
      brand: ($('trh-pillow-contrast-com-brand') && $('trh-pillow-contrast-com-brand').value.trim()) || '',
      color: ($('trh-pillow-contrast-com-color') && $('trh-pillow-contrast-com-color').value.trim()) || '',
      link: ($('trh-pillow-contrast-com-link') && $('trh-pillow-contrast-com-link').value.trim()) || '',
      notes: ($('trh-pillow-contrast-com-notes') && $('trh-pillow-contrast-com-notes').value.trim()) || ''
    };
  }

  /**
   * Builds the array of line items to send to /cart/add.js. Each item
   * includes the variant ID, quantity, and line item properties.
   *
   * @returns {Array<Object>} Array of cart line items
   */
  function buildCartItems() {
    var items = [];
    var productTitle = _config.productTitle || 'Made-to-Order Pillow';
    var userQuantity = getFormQuantity();
    var finish = EDGE_FINISH_MAP[_currentEdgeFinish];
    var yardage = lookupYardage(_config.pillowType, _currentSize, _currentEdgeFinish);

    if (!yardage || !finish) return items;

    // Generate a unique group ID to link all items together in the cart
    var groupId = 'g' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4);

    // Item 1: The pillow variant itself
    var pillowProperties = {
      '_group_id': groupId,
      'Size': _currentSize,
      'Edge Finish': _currentEdgeFinish
    };

    if (finish.isCOM || _ownFabric) {
      var com = collectCOMFields();
      pillowProperties['Fabric'] = 'COM' + (com.name ? ' - ' + com.name : '');
      if (com.brand) pillowProperties['Fabric Brand'] = com.brand;
      if (com.color) pillowProperties['Fabric Pattern Color'] = com.color;
      if (com.link) pillowProperties['Fabric Link'] = com.link;
      if (com.notes) pillowProperties['Fabric Notes'] = com.notes;
    } else if (_selectedFabric) {
      pillowProperties['Fabric'] = _selectedFabric.title;
    }

    if (finish.needsTrim) {
      if (_ownTrim) {
        var trimCom = collectTrimCOMFields();
        pillowProperties['Trim'] = 'COM' + (trimCom.name ? ' - ' + trimCom.name : '');
        if (trimCom.brand) pillowProperties['Trim Brand'] = trimCom.brand;
        if (trimCom.color) pillowProperties['Trim Pattern Color'] = trimCom.color;
        if (trimCom.link) pillowProperties['Trim Link'] = trimCom.link;
        if (trimCom.notes) pillowProperties['Trim Notes'] = trimCom.notes;
      } else if (_selectedTrim) {
        pillowProperties['Trim'] = _selectedTrim.title;
      }
    }

    if (finish.needsContrast) {
      if (_ownContrast) {
        var contrastCom = collectContrastCOMFields();
        pillowProperties['Contrast Fabric'] = 'COM' + (contrastCom.name ? ' - ' + contrastCom.name : '');
        if (contrastCom.brand) pillowProperties['Contrast Fabric Brand'] = contrastCom.brand;
        if (contrastCom.color) pillowProperties['Contrast Fabric Pattern Color'] = contrastCom.color;
        if (contrastCom.link) pillowProperties['Contrast Fabric Link'] = contrastCom.link;
        if (contrastCom.notes) pillowProperties['Contrast Fabric Notes'] = contrastCom.notes;
      } else if (_selectedContrastFabric) {
        pillowProperties['Contrast Fabric'] = _selectedContrastFabric.title;
      }
    }

    items.push({
      id: parseInt(_currentVariantId, 10),
      quantity: userQuantity,
      properties: pillowProperties
    });

    // Item 2: Main fabric (not added for COM or own fabric)
    if (!finish.isCOM && !_ownFabric && _selectedFabric) {
      var rawFabricQty = yardage.fabricYardage * userQuantity;
      var fabricQty = applyMinimum(rawFabricQty, 'fabric');
      items.push({
        id: parseInt(_selectedFabric.id, 10),
        quantity: fabricQty,
        properties: {
          '_group_id': groupId,
          '_is_sub_item': 'true',
          'For': productTitle,
          'Pillow Size': _currentSize,
          'Seam Finish': _currentEdgeFinish
        }
      });
    }

    // Item 3 (conditional): Trim (not added for own trim)
    if (finish.needsTrim && !_ownTrim && _selectedTrim && yardage.trimYardage > 0) {
      var rawTrimQty = yardage.trimYardage * userQuantity;
      var trimQty = applyMinimum(rawTrimQty, 'trim');
      items.push({
        id: parseInt(_selectedTrim.id, 10),
        quantity: trimQty,
        properties: {
          '_group_id': groupId,
          '_is_sub_item': 'true',
          'For': productTitle,
          'Pillow Size': _currentSize,
          'Seam Finish': _currentEdgeFinish
        }
      });
    }

    // Item 3/4 (conditional): Contrast fabric for welt (not added for own contrast)
    if (finish.needsContrast && !_ownContrast && _selectedContrastFabric && yardage.contrastYardage > 0) {
      var rawContrastQty = yardage.contrastYardage * userQuantity;
      var contrastQty = applyMinimum(rawContrastQty, 'contrast');
      items.push({
        id: parseInt(_selectedContrastFabric.id, 10),
        quantity: contrastQty,
        properties: {
          '_group_id': groupId,
          '_is_sub_item': 'true',
          'For': productTitle + ' (Contrast Welt)',
          'Pillow Size': _currentSize,
          'Seam Finish': _currentEdgeFinish
        }
      });
    }

    return items;
  }

  /**
   * Handles the add-to-cart action. Validates selections, builds the
   * multi-item payload, and POSTs to Shopify's /cart/add.js endpoint.
   * On success, redirects to the cart page.
   *
   * @param {Event} [event] - The form submission event (will be prevented)
   */
  function handleAddToCart(event) {
    if (event) {
      event.preventDefault();
    }

    // Prevent double-submission
    if (_isSubmitting) return;

    // Validate
    var error = validateForCart();
    if (error) {
      showCartError(error);
      return;
    }

    var items = buildCartItems();
    if (items.length === 0) {
      showCartError('Unable to build cart items. Please verify your selections.');
      return;
    }

    _isSubmitting = true;
    updateSubmitButtonState(true);

    console.log(LOG_PREFIX, 'Adding to cart:', items);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (data) {
            throw new Error(data.description || data.message || 'Failed to add items to cart.');
          });
        }
        return response.json();
      })
      .then(function (data) {
        console.log(LOG_PREFIX, 'Successfully added to cart:', data);
        _isSubmitting = false;
        updateSubmitButtonState(false);

        // Redirect to cart or show success
        window.location.href = '/cart';
      })
      .catch(function (err) {
        console.error(LOG_PREFIX, 'Add to cart error:', err);
        _isSubmitting = false;
        updateSubmitButtonState(false);
        showCartError(err.message || 'An error occurred. Please try again.');
      });
  }

  /**
   * Shows an error message near the add-to-cart button.
   *
   * @param {string} message - Error message to display
   */
  function showCartError(message) {
    var statusEl = $('trh-pillow-cart-status');
    if (statusEl) {
      statusEl.textContent = message;
      statusEl.style.display = 'block';
      statusEl.style.background = '#faf1f1';
      statusEl.style.border = '1px solid #dfc1c1';
      statusEl.style.color = '#7a2f2f';
    } else {
      console.error(LOG_PREFIX, message);
      alert(message);
    }
  }

  /**
   * Hides the cart error/status message.
   */
  function hideCartError() {
    var statusEl = $('trh-pillow-cart-status');
    if (statusEl) {
      statusEl.textContent = '';
      statusEl.style.display = 'none';
    }
  }

  /**
   * Updates the submit/add-to-cart button's disabled state and label
   * during cart submission.
   *
   * @param {boolean} isLoading - Whether a submission is in progress
   */
  function updateSubmitButtonState(isLoading) {
    var btn = $('trh-pillow-add-to-cart');
    if (!btn) return;

    if (isLoading) {
      btn.disabled = true;
      btn.textContent = 'Adding...';
      btn.style.background = '#888';
    } else {
      btn.disabled = false;
      btn.textContent = 'Add to Cart';
      btn.style.background = '#1a1a1a';
    }
  }

  /* =========================================================================
   * EVENT HANDLING
   * ========================================================================= */

  /** @type {Array<Object>} Tracked event listeners for cleanup */
  var _boundListeners = [];

  /**
   * Adds an event listener and tracks it for cleanup on destroy.
   *
   * @param {EventTarget} target - DOM element or event target
   * @param {string} eventName - Event name
   * @param {Function} handler - Event handler
   * @param {Object} [options] - addEventListener options
   */
  function addTrackedListener(target, eventName, handler, options) {
    target.addEventListener(eventName, handler, options);
    _boundListeners.push({
      target: target,
      event: eventName,
      handler: handler,
      options: options
    });
  }

  /**
   * Removes all tracked event listeners. Called during cleanup/destroy.
   */
  function removeAllListeners() {
    _boundListeners.forEach(function (entry) {
      entry.target.removeEventListener(entry.event, entry.handler, entry.options);
    });
    _boundListeners = [];
  }

  /**
   * Handles the `trh:fabric-selected` custom event from TRHFabricBrowser.
   * Updates internal state with the selected fabric data.
   *
   * @param {CustomEvent} event - Event with fabric data in `detail.fabric`
   */
  function onFabricSelected(event) {
    var fabric = event.detail ? event.detail.fabric : null;
    if (!fabric) return;

    /* Route catalog-popup selections made via the contrast "Browse Fabrics"
     * button to the contrast slot instead of overwriting the main fabric. */
    if (_fabricBrowseTarget === 'contrast') {
      _fabricBrowseTarget = 'fabric';

      /* TRH welt 2026-10-26: contrast welt must be a sellable Fabric/Vinyl */
      if (window.TRHFabricBrowser && TRHFabricBrowser.isWeltFabric && !TRHFabricBrowser.isWeltFabric(fabric)) {
        if (_selectedFabric) TRHFabricBrowser.selectFabric(_selectedFabric); else TRHFabricBrowser.clearSelection('fabric');
        TRHFabricBrowser.weltBlocked(fabric);
        return;
      }

      _selectedContrastFabric = {
        id: fabric.id,
        title: fabric.title,
        price: fabric.price,
        imageUrl: fabric.imageUrl || '',
        minQty: fabric.minQty || null,
        unit: fabric.unit || null,
        tags: fabric.tags || null,
        vendor: fabric.vendor || ''
      };

      var contrastField = $('pillowContrastFabricField');
      if (contrastField) {
        contrastField.value = fabric.title;
        contrastField.classList.add('selected');
      }

      /* The shared browser also wrote this selection into ITS main-fabric
       * field/swatch UI — undo that without firing our cleared handler on
       * stale state (the browser's clear event is handled idempotently). */
      if (window.TRHFabricBrowser && _selectedFabric) {
        window.TRHFabricBrowser.selectFabric(_selectedFabric);
      } else if (window.TRHFabricBrowser) {
        window.TRHFabricBrowser.clearSelection('fabric');
      }

      console.log(LOG_PREFIX, 'Contrast fabric selected via catalog:', fabric.title);
      hideCartError();
      updatePricingDisplay();
      return;
    }

    _selectedFabric = {
      id: fabric.id,
      title: fabric.title,
      price: fabric.price,
      imageUrl: fabric.imageUrl || '',
      fabricWidth: fabric.fabricWidth || DEFAULT_FABRIC_WIDTH,
      minQty: fabric.minQty || null,
      unit: fabric.unit || null,
      tags: fabric.tags || null,
      vendor: fabric.vendor || ''
    };

    console.log(LOG_PREFIX, 'Fabric selected:', _selectedFabric.title);
    hideCartError();
    updateYardageDisplay();
  }

  /**
   * Handles the `trh:fabric-cleared` custom event from TRHFabricBrowser.
   * Clears the selected fabric from internal state.
   */
  function onFabricCleared() {
    _selectedFabric = null;
    console.log(LOG_PREFIX, 'Fabric selection cleared.');
    updateYardageDisplay();
  }

  /**
   * Handles the `trh:trim-selected` custom event from TRHFabricBrowser.
   * Updates internal state with the selected trim data.
   *
   * @param {CustomEvent} event - Event with trim data in `detail.trim`
   */
  function onTrimSelected(event) {
    var trim = event.detail ? event.detail.trim : null;
    if (!trim) return;

    _selectedTrim = {
      id: trim.id,
      title: trim.title,
      price: trim.price,
      imageUrl: trim.imageUrl || '',
      vendor: trim.vendor || '',
      tags: trim.tags || []
    };

    console.log(LOG_PREFIX, 'Trim selected:', _selectedTrim.title);
    hideCartError();
    updateYardageDisplay(); /* TRH trim min 2026-10-26: trim minimum depends on the trim chosen */
    updatePricingDisplay();
  }

  /**
   * Handles the `trh:trim-cleared` custom event from TRHFabricBrowser.
   * Clears the selected trim from internal state.
   */
  function onTrimCleared() {
    _selectedTrim = null;
    console.log(LOG_PREFIX, 'Trim selection cleared.');
    updateYardageDisplay();
    updatePricingDisplay();
  }

  /**
   * Handles contrast fabric selection. This listens for a dedicated
   * custom event dispatched when a contrast fabric is chosen (may
   * come from a second TRHFabricBrowser instance or a custom UI).
   *
   * @param {CustomEvent} event - Event with contrast fabric in `detail.fabric`
   */
  function onContrastFabricSelected(event) {
    var fabric = event.detail ? event.detail.fabric : null;
    if (!fabric) return;

    _selectedContrastFabric = {
      id: fabric.id,
      title: fabric.title,
      price: fabric.price,
      imageUrl: fabric.imageUrl || ''
    };

    console.log(LOG_PREFIX, 'Contrast fabric selected:', _selectedContrastFabric.title);
    hideCartError();
    updatePricingDisplay();
  }

  /**
   * Handles contrast fabric cleared event.
   */
  function onContrastFabricCleared() {
    _selectedContrastFabric = null;
    console.log(LOG_PREFIX, 'Contrast fabric selection cleared.');
    updatePricingDisplay();
  }

  /** @type {number} Sequence counter guarding against stale async search renders */
  var _contrastSearchSeq = 0;

  /**
   * Shows search suggestions for the contrast fabric field. Searches the
   * FULL fabric inventory server-side via TRHFabricBrowser.searchFabrics()
   * (the store has 5,000+ fabrics; getAllFabrics() is a bounded cache and
   * must not be used for search).
   * @param {string} query - Search query string
   */
  async function showContrastSuggestions(query) {
    var suggestionsEl = $('pillowContrastSuggestions');
    var field = $('pillowContrastFabricField');
    if (!suggestionsEl) return;

    var normalized = (query || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    if (!normalized || normalized.length < 1) {
      suggestionsEl.style.display = 'none';
      suggestionsEl.innerHTML = '';
      return;
    }

    var seq = ++_contrastSearchSeq;
    suggestionsEl.innerHTML = '<div style="padding:12px;font-size:13px;color:#999;">Searching\u2026</div>';
    suggestionsEl.style.display = 'block';

    var matches = [];
    if (typeof window.TRHFabricBrowser !== 'undefined' &&
        typeof window.TRHFabricBrowser.searchFabrics === 'function') {
      try {
        matches = await window.TRHFabricBrowser.searchFabrics(query, 10);
      } catch (err) {
        console.error(LOG_PREFIX, 'Contrast fabric search failed:', err);
        if (seq !== _contrastSearchSeq) return;
        suggestionsEl.innerHTML = '<div style="padding:12px;font-size:13px;color:#999;">Search is temporarily unavailable. Please try again.</div>';
        return;
      }
    }

    if (seq !== _contrastSearchSeq) return; /* superseded by a newer keystroke */

    suggestionsEl.innerHTML = '';

    if (matches.length === 0) {
      suggestionsEl.innerHTML = '<div style="padding:12px;font-size:13px;color:#999;">No fabrics found</div>';
    } else {
      matches.forEach(function (item) {
        var row = document.createElement('div');
        row.style.cssText = 'padding:10px 12px;cursor:pointer;border-bottom:1px solid #eee;font-size:14px;display:flex;align-items:center;gap:10px;';
        row.addEventListener('mouseenter', function () { row.style.background = '#f0f8ff'; });
        row.addEventListener('mouseleave', function () { row.style.background = ''; });

        var img = document.createElement('img');
        img.src = item.imageUrl;
        img.alt = item.title;
        img.style.cssText = 'width:40px;height:40px;object-fit:cover;border-radius:4px;border:1px solid #ddd;flex-shrink:0;';

        var details = document.createElement('div');
        details.innerHTML = '<div style="font-weight:500;">' + escapeHTML(item.title) + '</div>' +
          '<div style="font-size:12px;color:#666;">$' + item.price.toFixed(2) + _FM().per(item) + '</div>';

        row.appendChild(img);
        row.appendChild(details);

        row.addEventListener('click', function () {
          _selectedContrastFabric = {
            id: item.id,
            title: item.title,
            price: item.price,
            imageUrl: item.imageUrl || ''
          };
          if (field) {
            field.value = item.title;
            field.classList.add('selected');
          }
          suggestionsEl.style.display = 'none';
          suggestionsEl.innerHTML = '';
          console.log(LOG_PREFIX, 'Contrast fabric selected via search:', item.title);
          hideCartError();
          updatePricingDisplay();
        });

        suggestionsEl.appendChild(row);
      });
    }

    suggestionsEl.style.display = 'block';
  }

  /**
   * Escapes HTML entities for safe display.
   * @param {string} str - Raw string
   * @returns {string} Escaped string
   */
  function escapeHTML(str) {
    if (!str) return '';
    var d = document.createElement('div');
    d.appendChild(document.createTextNode(str));
    return d.innerHTML;
  }

  /**
   * Binds all event listeners needed by the pillow configurator:
   *   - Variant option change detection (size + edge finish selectors)
   *   - TRHFabricBrowser custom events (fabric/trim selected/cleared)
   *   - Product form submission interception
   */
  function bindEvents() {
    // COM toggle checkboxes
    var ownFabricCb = $('pillowOwnFabric');
    if (ownFabricCb) {
      addTrackedListener(ownFabricCb, 'change', handleOwnFabricToggle);
    }
    var ownTrimCb = $('pillowOwnTrim');
    if (ownTrimCb) {
      addTrackedListener(ownTrimCb, 'change', handleOwnTrimToggle);
    }
    var ownContrastCb = $('pillowOwnContrast');
    if (ownContrastCb) {
      addTrackedListener(ownContrastCb, 'change', handleOwnContrastToggle);
    }

    // Contrast fabric search autocomplete (uses TRHFabricBrowser.searchFabrics())
    var contrastField = $('pillowContrastFabricField');
    var contrastSuggestions = $('pillowContrastSuggestions');
    if (contrastField && contrastSuggestions) {
      var contrastDebounce = null;
      addTrackedListener(contrastField, 'input', function () {
        clearTimeout(contrastDebounce);
        contrastDebounce = setTimeout(function () {
          showContrastSuggestions(contrastField.value);
        }, 200);
      });
      addTrackedListener(contrastField, 'focus', function () {
        if (contrastField.value.length > 0) {
          showContrastSuggestions(contrastField.value);
        }
      });
      addTrackedListener(document, 'click', function (e) {
        if (!contrastField.contains(e.target) && !contrastSuggestions.contains(e.target)) {
          contrastSuggestions.style.display = 'none';
        }
      });
    }

    // Listen for TRHFabricBrowser custom events
    addTrackedListener(document, 'trh:fabric-selected', onFabricSelected);
    addTrackedListener(document, 'trh:fabric-cleared', onFabricCleared);
    addTrackedListener(document, 'trh:trim-selected', onTrimSelected);
    addTrackedListener(document, 'trh:trim-cleared', onTrimCleared);

    // Listen for contrast fabric events (custom, used by a second fabric browser)
    addTrackedListener(document, 'trh:contrast-fabric-selected', onContrastFabricSelected);
    addTrackedListener(document, 'trh:contrast-fabric-cleared', onContrastFabricCleared);

    // Intercept variant option changes on <select> elements
    var variantSelects = document.querySelectorAll(
      'select[name*="option"], select[data-option-name], .product-form__input select'
    );
    for (var i = 0; i < variantSelects.length; i++) {
      addTrackedListener(variantSelects[i], 'change', function () {
        syncVariantOptions();
      });
    }

    // Intercept variant option changes on radio buttons
    var variantRadios = document.querySelectorAll(
      '.product-form__input input[type="radio"], fieldset input[type="radio"]'
    );
    for (var j = 0; j < variantRadios.length; j++) {
      addTrackedListener(variantRadios[j], 'change', function () {
        syncVariantOptions();
      });
    }

    // Watch for hidden variant ID changes (some themes update this via JS)
    // Watch for variant ID changes via the hidden form input
    var form = document.querySelector(
      'form[action*="/cart/add"], form.product-form, form[data-type="add-to-cart-form"]'
    );
    if (form) {
      var idInput = form.querySelector('input[name="id"]');
      if (idInput) {
        var observer = new MutationObserver(function () {
          syncVariantOptions();
        });
        observer.observe(idInput, { attributes: true, attributeFilter: ['value'] });
      }
    }

    // Quantity change — recalculate yardage and pricing when quantity changes
    if (form) {
      var qtyInput = form.querySelector('input[name="quantity"]');
      if (qtyInput) {
        addTrackedListener(qtyInput, 'change', function () {
          updateYardageDisplay();
        });
        addTrackedListener(qtyInput, 'input', function () {
          updateYardageDisplay();
        });
      }
    }

    // Use our own dedicated Add to Cart button — no form interception needed
    var trhAddBtn = $('trh-pillow-add-to-cart');
    if (trhAddBtn) {
      addTrackedListener(trhAddBtn, 'click', function (e) {
        e.preventDefault();
        handleAddToCart(e);
      });
    }
  }

  /* =========================================================================
   * INITIALIZATION & CLEANUP
   * ========================================================================= */

  /**
   * Cleans up all event listeners and resets module state. Can be called
   * before re-initialization or when the module is no longer needed.
   */
  function cleanup() {
    removeAllListeners();
    _config = null;
    _selectedFabric = null;
    _selectedTrim = null;
    _selectedContrastFabric = null;
    _currentSize = null;
    _currentEdgeFinish = null;
    _currentVariantId = null;
    _isSubmitting = false;
    _ownFabric = false;
    _ownTrim = false;
    _ownContrast = false;
    _comFields = null;
  }

  /* =========================================================================
   * PUBLIC API
   * ========================================================================= */

  /**
   * @namespace TRHPillowConfigurator
   * @description Browser-side pillow configurator module for The ReBorn House.
   * Manages fabric selection, yardage lookup, and multi-item add-to-cart
   * for Made-to-Order pillow products.
   */
  window.TRHPillowConfigurator = {

    /**
     * Sets where the next catalog-popup fabric selection should land.
     * Call with 'contrast' right before opening the fabric catalog from the
     * contrast "Browse Fabrics" button; resets to 'fabric' automatically
     * after the next selection.
     * @param {string} target - 'fabric' or 'contrast'
     */
    setFabricBrowseTarget: function (target) {
      _fabricBrowseTarget = target === 'contrast' ? 'contrast' : 'fabric';
    },

    /**
     * Initializes the pillow configurator module. Binds event listeners,
     * reads initial variant option state, and prepares the add-to-cart
     * interception.
     *
     * Must be called after the DOM is ready and after the product form
     * has been rendered by Shopify.
     *
     * @param {Object} config - Configuration object
     * @param {string} config.pillowType - Pillow type: 'throw', 'lumbar', or 'bolster'
     * @param {string} config.productTitle - Product title for line item properties
     *   (e.g., "Throw Pillow", "Lumbar Pillow")
     *
     * @example
     * TRHPillowConfigurator.init({
     *   pillowType: 'throw',
     *   productTitle: 'Custom Throw Pillow'
     * });
     */
    init: function (config) {
      // Clean up any previous initialization
      if (_config) {
        cleanup();
      }

      // Validate required config
      if (!config || !config.pillowType) {
        console.error(LOG_PREFIX, 'config.pillowType is required (throw, lumbar, or bolster).');
        return;
      }

      var validTypes = ['throw', 'lumbar', 'bolster'];
      if (validTypes.indexOf(config.pillowType.toLowerCase()) === -1) {
        console.error(LOG_PREFIX, 'Invalid pillowType. Must be one of:', validTypes.join(', '));
        return;
      }

      _config = {
        pillowType: config.pillowType.toLowerCase(),
        productTitle: config.productTitle || 'Made-to-Order Pillow'
      };

      // Bind events and read initial state
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
          bindEvents();
          syncVariantOptions();
          console.log(LOG_PREFIX, 'Initialized.', {
            pillowType: _config.pillowType,
            productTitle: _config.productTitle
          });
        });
      } else {
        bindEvents();
        syncVariantOptions();
        console.log(LOG_PREFIX, 'Initialized.', {
          pillowType: _config.pillowType,
          productTitle: _config.productTitle
        });
      }
    },

    /**
     * Returns the current yardage calculation based on the selected variant
     * options (size + edge finish). Returns null if options are not yet
     * selected or if no yardage data exists for the combination.
     *
     * @returns {Object|null} Yardage result object with fabricYardage,
     *   trimYardage, contrastYardage, and lookupKey; or null
     */
    getYardage: function () {
      if (!_config || !_currentSize || !_currentEdgeFinish) {
        return null;
      }
      return lookupYardage(_config.pillowType, _currentSize, _currentEdgeFinish);
    },

    /**
     * Returns a snapshot of the current module state including selected
     * fabric, trim, contrast fabric, variant options, and yardage.
     *
     * @returns {Object} Current state snapshot
     * @returns {Object|null} return.config - Current configuration
     * @returns {Object|null} return.selectedFabric - Selected main fabric
     * @returns {Object|null} return.selectedTrim - Selected trim
     * @returns {Object|null} return.selectedContrastFabric - Selected contrast fabric
     * @returns {string|null} return.currentSize - Currently selected size
     * @returns {string|null} return.currentEdgeFinish - Currently selected edge finish
     * @returns {string|null} return.currentVariantId - Currently selected variant ID
     * @returns {Object|null} return.yardage - Current yardage lookup result
     */
    getState: function () {
      return {
        config: _config ? { pillowType: _config.pillowType, productTitle: _config.productTitle } : null,
        selectedFabric: _selectedFabric,
        selectedTrim: _selectedTrim,
        selectedContrastFabric: _selectedContrastFabric,
        currentSize: _currentSize,
        currentEdgeFinish: _currentEdgeFinish,
        currentVariantId: _currentVariantId,
        yardage: (_config && _currentSize && _currentEdgeFinish)
          ? lookupYardage(_config.pillowType, _currentSize, _currentEdgeFinish)
          : null
      };
    },

    /**
     * Destroys the module instance. Removes all event listeners and resets
     * all internal state. The module can be re-initialized by calling
     * init() again.
     */
    destroy: function () {
      cleanup();
      console.log(LOG_PREFIX, 'Destroyed.');
    }
  };
})();
